import { type RefObject, useEffect, useRef } from "react";

const isFullyVisibleWithinContainer = (element: HTMLElement, container: HTMLElement): boolean => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return (
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom &&
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right
  );
};

interface UseFocusSearchOnCmdFOptions {
  disabled?: boolean;
}

/**
 * Intercepts Cmd/Ctrl+F to focus a search input instead of opening browser find.
 * - Scrolls the input into view first if it isn't fully visible within `scrollContainerRef`.
 * - Does nothing, letting native find open, when `options.disabled` is true or the input ref isn't resolved.
 * - If multiple instances can be mounted concurrently (e.g. one per tab), only the active one should have `disabled: false`.
 */
export const useFocusSearchOnCmdF = (
  ref: RefObject<HTMLInputElement | null>,
  scrollContainerRef: RefObject<HTMLElement | null>,
  options?: UseFocusSearchOnCmdFOptions
): void => {
  const hasWarnedMissingContainerRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "f") return;
      if (options?.disabled) return;
      const input = ref.current;
      if (!input) return;

      event.preventDefault();

      const container = scrollContainerRef.current;
      if (!container) {
        if (import.meta.env.DEV && !hasWarnedMissingContainerRef.current) {
          hasWarnedMissingContainerRef.current = true;
          console.warn(
            "[useFocusSearchOnCmdF] scrollContainerRef.current is null — skipping scroll-into-view."
          );
        }
      } else if (!isFullyVisibleWithinContainer(input, container)) {
        input.scrollIntoView({ behavior: "auto", block: "center" });
      }

      input.focus();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [ref, scrollContainerRef, options?.disabled]);
};
